import { Trash2, Share2, GalleryVerticalEnd } from 'lucide-react';
import ChatInput from '../ChatInput';
import ConversationListPanel from '../ConversationListPanel';
import BatchShareModal from '../BatchShareModal';
import { showToast } from '../Toast';
import { Conversation } from '@/types';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { useEffect, useState } from 'react';

// Hook to force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

interface ConversationSectionProps {
  // Chat input props
  projectId: string;
  projectName: string;
  isViewer?: boolean; // 🔥 是否为 viewer 角色（只读模式）
  projectArchived?: boolean; // 🔥 项目是否已归档
  onNewChat: (content: string, isRetry?: boolean, options?: any) => void;

  // Conversation list props
  conversations: Conversation[];
  visibleConversations: Conversation[];
  filteredConversations: Conversation[];
  isLoading: boolean;
  searchQuery: string;
  selectedConversationIds: Set<string>;
  hasMoreConversations: boolean;
  onConversationSelect: (conversationId: string) => void;
  onToggleSelect: (id: string, e: React.MouseEvent) => void;
  onMenuAction: (
    action: string,
    conversationId: string,
    event: React.MouseEvent
  ) => void;
  onBatchSelect: (ids: string[]) => void;
  onShowMore: () => void;
  formatDate: (date: string) => string;

  // Selection toolbar props
  onBulkDelete: () => void;
  onRemoveFromProject: () => void;
  onSelectAll: () => void;
}

export default function ConversationSection({
  // Chat input props
  projectId,
  projectName,
  isViewer = false,
  projectArchived = false,
  onNewChat,

  // Conversation list props
  filteredConversations,
  visibleConversations,
  isLoading,
  searchQuery,
  selectedConversationIds,
  hasMoreConversations,
  onConversationSelect,
  onToggleSelect,
  onMenuAction,
  onBatchSelect,
  onShowMore,
  formatDate,

  // Selection toolbar props
  onBulkDelete,
  onRemoveFromProject,
  onSelectAll,
}: ConversationSectionProps) {
  const { t } = useTranslation();
  useForceUpdate();

  // 批量分享状态
  const [batchShareModal, setBatchShareModal] = useState<{
    isOpen: boolean;
    conversationIds: string[];
    conversationTitles: string[];
  }>({
    isOpen: false,
    conversationIds: [],
    conversationTitles: [],
  });

  // 批量分享处理函数
  const handleBatchShare = () => {
    if (selectedConversationIds.size === 0) return;

    const selectedConversations = visibleConversations.filter(conv =>
      selectedConversationIds.has(conv.conversationId)
    );

    const conversationIds = selectedConversations.map(c => c.conversationId);
    const conversationTitles = selectedConversations.map(c => c.title);

    setBatchShareModal({
      isOpen: true,
      conversationIds,
      conversationTitles,
    });
  };

  const visibleIds = visibleConversations.map(c => c.conversationId);
  const allVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every(id => selectedConversationIds.has(id));

  return (
    <>
      {/* Chat Input Card */}
      <div
        className={`${filteredConversations.length > 0 && 'border-b border-gray-300'} pb-1 sm:pb-2`}
      >
        {/* 🔥 viewer 角色禁用输入框 */}
        <ChatInput
          onSendMessage={onNewChat}
          placeholder={
            isViewer
              ? t('project.viewersCannotSend')
              : t('chat.replyPlaceholder') || 'Reply...'
          }
          projectId={projectId}
          projectName={projectName}
          centered={false}
          currentView={'project-detail'}
          isLoading={false}
          disabled={isViewer}
        />
      </div>

      {/* Conversation Selection toolbar */}
      {selectedConversationIds.size > 0 && (
        <div className="flex items-center justify-between py-2 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onSelectAll}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
            >
              <div className="shrink-0 w-4 h-4 flex items-center justify-center rounded cursor-pointer bg-[#2c84db]">
                {allVisibleSelected ? (
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M2 12H20"
                      strokeWidth="2.5"
                      stroke="currentColor"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                )}
              </div>
              <span className="font-normal text-gray-700 ml-2">
                {selectedConversationIds.size} {t('project.selected')}
              </span>
            </button>
            {/* 🔥 viewer 角色禁用批量操作 */}
            <button
              onClick={onRemoveFromProject}
              disabled={isViewer}
              className={`p-2 rounded transition-colors ${
                isViewer
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-[#f0f1ed]'
              }`}
              title={
                isViewer
                  ? t('project.viewersCannotRemove')
                  : `${t('project.removeFromProject')}`
              }
            >
              <GalleryVerticalEnd size={20} className="text-gray-600" />
            </button>
            <button
              onClick={handleBatchShare}
              className="p-2 hover:bg-[#f0f1ed] rounded transition-colors"
              title={t('tooltips.batchShare') || 'Batch Share'}
            >
              <Share2 size={20} className="text-gray-600" />
            </button>
            <button
              onClick={onBulkDelete}
              disabled={isViewer}
              className={`p-2 rounded transition-colors ${
                isViewer
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-[#f0f1ed]'
              }`}
              title={
                isViewer
                  ? t('project.viewersCannotDelete')
                  : `${t('common.delete')} ${selectedConversationIds.size} ${t('project.selectedItems')}`
              }
            >
              <Trash2 size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* Conversations List */}
      <div className="flex-1 min-h-0 border-b border-gray-300 overflow-hidden">
        <ConversationListPanel
          conversations={visibleConversations}
          isLoading={isLoading}
          searchQuery={searchQuery}
          selectedIds={selectedConversationIds}
          isViewer={isViewer}
          projectArchived={projectArchived}
          onConversationSelect={onConversationSelect}
          onToggleSelect={onToggleSelect}
          onMenuAction={onMenuAction}
          onBatchSelect={onBatchSelect}
          hasMore={hasMoreConversations}
          onShowMore={onShowMore}
          showMoreLabel={t('project.moreChats')}
          showProjectMenu={false}
          containerClassName="max-h-[55dvh] xl:h-full xl:max-h-none"
          formatDate={formatDate}
        />
      </div>

      {/* Batch Share Modal */}
      <BatchShareModal
        isOpen={batchShareModal.isOpen}
        onClose={() =>
          setBatchShareModal({
            isOpen: false,
            conversationIds: [],
            conversationTitles: [],
          })
        }
        conversationIds={batchShareModal.conversationIds}
        conversationTitles={batchShareModal.conversationTitles}
        projectId={projectId}
        onSuccess={() => {
          setBatchShareModal({
            isOpen: false,
            conversationIds: [],
            conversationTitles: [],
          });
          showToast(t('toast.batchShareSuccess') || '批量分享成功', 'success');
        }}
      />
    </>
  );
}
